import { shouldShowNavigationLink } from 'utils/cms/navigationHelpers';

const CMS_MANAGED_THUNDR_PAGES = ['boltGallery', 'boltHome', 'boltHomeEnt', 'boltPreviewPanel', 'boltShowtimes', 'boltSynopsis', 'boltVideos', 'boltWatchMore'];
const statusDrivenPages = ['showtimes', 'homeEnt'];

function pageIsDisabled(pageId, cmsData) {
	if (pageId === 'home') return false; // Home is always enabled
	return !cmsData?.pages?.[pageId]?.active;
}

function getCmsDefaultPages(cmsData) {
	const homeThundrId = cmsData?.pages?.home?.heroMode === 'preview' ? 'boltPreviewPanel' : 'boltHome';
	return [
		{ id: 'home', destination: homeThundrId, name: 'Home', order: 0 },
		{ id: 'synopsis', destination: 'boltSynopsis', name: 'Synopsis', order: 1 },
		{ id: 'gallery', destination: 'boltGallery', name: 'Gallery', order: 2 },
		{ id: 'videos', destination: 'boltVideos', name: 'Videos', order: 3 },
		{ id: 'showtimes', destination: 'boltShowtimes', name: 'Showtimes', order: 4 },
		{ id: 'homeEnt', destination: 'boltHomeEnt', name: 'Watch at Home', order: 5 },
	];
}

function getText(value, appData) {
	const { accessibilityCopy, baseCopy } = appData?.copy || {};
	return baseCopy?.[value] || accessibilityCopy?.[value] || value || '';
}

function getDefaultLinks(cmsData, appData) {
	const thundrPages = appData?.pages || {};

	// Map CMS defaults
	const defaultPages = Object.fromEntries(
		getCmsDefaultPages(cmsData).map((page, i) => [
			page.id,
			{
				id: page.id,
				destination: page.destination,
				name: getText(thundrPages[page.destination]?.name || page.name, appData),
				order: i,
				default: true,
				permanent: page.id === 'home',
			},
		])
	);

	// Add any additional Bolt pages that aren't in the CMS defaults
	Object.entries(thundrPages)
		.filter(([thundrId, pageData]) => pageData.base === 'bolt' && !CMS_MANAGED_THUNDR_PAGES.includes(thundrId))
		.forEach(([thundrId, pageData]) => {
			const name = getText(pageData.name || thundrId, appData);
			const order = Object.keys(defaultPages).length;
			defaultPages[thundrId] = { id: thundrId, name, destination: thundrId, order };
		});

	return defaultPages;
}

function mapNav(cmsData, appData) {
	const links = cmsData?.header?.links || {};
	const maxOrder = Math.max(...Object.values(links).map(link => link.order), 0);
	
	// Add missing default links to the links object
	const defaultLinks = getDefaultLinks(cmsData, appData);
	Object.entries(defaultLinks)
		.filter(([id]) => !links[id])
		.forEach(([id, link]) => links[id] = { ...link, order: maxOrder + link.order });

	// Recompute disabled state from current page toggles so saved/custom links
	// reflect the latest toggle values (ensureOverride bakes in a stale disabled flag).
	// Showtimes/homeEnt visibility is status-driven (ticket dates, homeEnt dates) so
	// we clear their disabled flag and let shouldShowNavigationLink handle them.
	Object.values(links).forEach(link => {
		if (!link.id || !cmsData?.pages?.[link.id]) return;
		if (statusDrivenPages.includes(link.id)) {
			delete link.disabled;
		} else {
			link.disabled = pageIsDisabled(link.id, cmsData);
		}
	});

	// Process to regular links
	const homeThundrId = cmsData?.pages?.home?.heroMode === 'preview' ? 'boltPreviewPanel' : 'boltHome';
	const nav = Object.values(links)
		.filter(link => shouldShowNavigationLink(link, cmsData))
		.reduce((acc, item) => {
			acc[item.id] = {
				linkType: 'primary',
				name: item.name,
				order: item.order,
				path: item.id === 'home' || item.destination === 'boltHome' ? homeThundrId : (item.thundrId || item.destination),
			};
			return acc;
		}, {});

	return nav;
}

function mapSocials(cmsData) {
	const socialsData = cmsData?.pages?.home?.socials;
	if (!socialsData) return {};

	const { facebook, instagram, tiktok, twitter, youtube } = socialsData;
	return {
		facebook: facebook?.enabled ? facebook : null,
		instagram: instagram?.enabled ? instagram : null,
		tiktok: tiktok?.enabled ? tiktok : null,
		twitter: twitter?.enabled ? twitter : null,
		youtube: youtube?.enabled ? youtube : null,
	};
}

export default function mapHeader(cmsData, appData) {
	return {
		powsterHeader: {
			follow: mapSocials(cmsData),
			nav: mapNav(cmsData, appData),
		}
	};
}